<template>
    <div class="contractor-management-page">
        <app-sub-title text="Gestión de Contratista" />
        <p class="text-muted">Aprobar o rechazar los documentos subidos por los contratistas. Al rechazar, se notificará al correo del contratista.</p>

        <div class="form-row contractor-mgmt-filters mb-3">
            <div class="col-12 col-sm-auto pr-sm-3 mb-3 mb-sm-0">
                <label class="contractor-mgmt-filter-label mb-1" for="contractor-mgmt-status">Estado</label>
                <b-form-select
                    id="contractor-mgmt-status"
                    v-model="statusFilter"
                    :options="statusOptions"
                    value-field="value"
                    text-field="text"
                    class="contractor-mgmt-select-estado"
                    @change="applyFilters"
                />
            </div>
            <div class="col-12 col-sm-auto">
                <label class="contractor-mgmt-filter-label mb-1" for="contractor-mgmt-document">Documento</label>
                <b-input-group>
                    <b-form-input
                        id="contractor-mgmt-document"
                        v-model="documentFilter"
                        type="text"
                        placeholder="Buscar por identificación"
                        @keyup.enter="applyFilters"
                    />
                    <b-input-group-append>
                        <b-button variant="outline-secondary" @click="applyFilters">
                            Buscar
                        </b-button>
                    </b-input-group-append>
                </b-input-group>
            </div>
        </div>

        <b-table
            :items="items"
            :fields="fields"
            :busy="loading"
            striped
            responsive
            show-empty
            empty-text="No hay documentos para revisar"
            head-variant="light"
        >
            <template #table-busy>
                <div class="text-center py-2">
                    <b-spinner small></b-spinner>
                    <span class="ml-2">Cargando...</span>
                </div>
            </template>
            <template #cell(nombre)="row">
                <div>
                    <strong>{{ row.item.contractor && row.item.contractor.user ? row.item.contractor.user.name : '—' }}</strong>
                    <br>
                    <small class="text-muted">{{ row.item.contractor && row.item.contractor.user ? row.item.contractor.user.email : '' }}</small>
                </div>
            </template>
            <template #cell(documento)="row">
                {{ row.item.contractor && row.item.contractor.user ? row.item.contractor.user.document : '—' }}
            </template>
            <template #cell(fecha_inicio)="row">
                {{ row.item.authorization_start_date ? formatDate(row.item.authorization_start_date) : '—' }}
            </template>
            <template #cell(fecha_fin)="row">
                {{ row.item.authorization_end_date ? formatDate(row.item.authorization_end_date) : '—' }}
            </template>
            <template #cell(status)="row">
                <b-badge :variant="statusVariant(row.item.status)">{{ statusLabel(row.item.status) }}</b-badge>
            </template>
            <template #cell(reviewed_by)="row">
                <span v-if="row.item.reviewed_by">{{ row.item.reviewed_by.name }}</span>
                <span v-else>—</span>
            </template>
            <template #cell(actions)="row">
                <b-button size="sm" variant="outline-primary" @click="openDocumentsModal(row.item)" title="Ver documentos del contratista">
                    <i class="fa fa-folder-open"></i> Ver documentos
                </b-button>
            </template>
        </b-table>

        <b-pagination
            v-if="pagination.last_page > 1"
            v-model="pagination.current_page"
            :total-rows="pagination.total"
            :per-page="pagination.per_page"
            align="right"
            @input="onPageChange"
        />

        <app-pop-up
            id="contractor-documents-modal"
            :title="documentsModalTitle"
            :on-close="closeDocumentsModal"
            size="large"
        >
            <div slot="body">
                <div v-if="documentsLoading" class="text-center py-4">
                    <b-spinner></b-spinner>
                    <p class="mt-2 mb-0">Cargando documentos...</p>
                </div>
                <div v-else-if="documentsData.items && documentsData.items.length" class="contractor-documents-list">
                    <div
                        v-for="(entry, index) in documentsData.items"
                        :key="entry.document_type.id"
                        class="border rounded p-3 mb-3"
                    >
                        <h6 class="mb-2">
                            {{ entry.document_type.name }}
                        </h6>
                        <p class="mb-2 text-muted small">
                            Estado:
                            <b-badge :variant="statusVariant(docStatus(entry))">{{ statusLabel(docStatus(entry)) }}</b-badge>
                        </p>
                        <p
                            v-if="entry.document && entry.document.expiry_date"
                            class="mb-2 small"
                            :class="isContractorDocumentExpired(entry.document) ? 'text-danger' : 'text-muted'"
                        >
                            <strong>Fecha de vencimiento:</strong>
                            {{ formatDate(entry.document.expiry_date) }}
                            <span v-if="isContractorDocumentExpired(entry.document)"> (vencido)</span>
                        </p>
                        <!-- Pending: contractor hasn't sent file -->
                        <template v-if="!entry.document">
                            <span class="text-muted">El contratista aún no ha subido este documento.</span>
                        </template>
                        <!-- Has document: show according to status -->
                        <template v-else>
                            <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                                <b-button class="mr-2 mb-2" size="sm" variant="outline-primary" @click="viewDocument(entry.document)" :disabled="viewLoading === entry.document.id">
                                    <i class="fa fa-eye"></i> Ver archivo
                                </b-button>
                                <template v-if="entry.document.status === 'pending_approval'">
                                    <b-button class="mr-2 mb-2" size="sm" variant="success" @click="approveDocInModal(entry.document)" :disabled="actionLoading === entry.document.id">
                                        <i class="fa fa-check"></i> Aprobar
                                    </b-button>
                                    <b-button class="mr-2 mb-2" size="sm" variant="danger" @click="toggleRejectInline(entry)" :disabled="actionLoading === entry.document.id">
                                        <i class="fa fa-times"></i> Rechazar
                                    </b-button>
                                </template>
                            </div>
                            <!-- Inline reject form (pending_approval) -->
                            <div v-if="rejectInlineDoc && rejectInlineDoc.document && rejectInlineDoc.document.id === entry.document.id" class="mt-2 pt-2 border-top">
                                <b-form-group label="Motivo del rechazo" class="mb-2">
                                    <b-form-textarea
                                        v-model="rejectInlineReason"
                                        rows="3"
                                        placeholder="Mínimo 10 caracteres. Se notificará al contratista por correo."
                                    />
                                    <small v-if="rejectInlineError" class="text-danger">{{ rejectInlineError }}</small>
                                </b-form-group>
                                <b-button size="sm" variant="secondary" class="mr-1" @click="cancelRejectInline">Cancelar</b-button>
                                <b-button size="sm" variant="danger" :disabled="rejectSending || !rejectInlineReason || rejectInlineReason.trim().length < 10" @click="submitRejectInline">
                                    <span v-if="rejectSending"><i class="fa fa-spinner fa-spin"></i> Enviando...</span>
                                    <span v-else>Rechazar y notificar</span>
                                </b-button>
                            </div>
                            <!-- Rejected: show reason (read-only) -->
                            <div v-if="entry.document.status === 'rejected' && entry.document.rejection_reason" class="mt-2">
                                <label class="small text-muted">Motivo del rechazo</label>
                                <b-form-input :value="entry.document.rejection_reason" readonly class="bg-light" />
                            </div>
                        </template>
                    </div>
                </div>
                <div v-else class="text-muted text-center py-3">
                    No hay tipos de documento configurados para este cliente.
                </div>
            </div>
        </app-pop-up>

        <app-pop-up
            id="contractor-management-reject-modal"
            title="Rechazar documento"
            :on-close="closeRejectModal"
        >
            <div slot="body">
                <p class="mb-2">Indique el motivo del rechazo. Se enviará un correo al contratista con este mensaje.</p>
                <b-form-group label="Motivo del rechazo" label-for="rejection-reason" :invalid-feedback="rejectErrors.rejection_reason">
                    <b-form-textarea
                        id="rejection-reason"
                        v-model="rejectForm.rejection_reason"
                        rows="4"
                        :state="rejectErrors.rejection_reason ? false : null"
                        placeholder="Ej: El documento está vencido. Por favor suba una copia actualizada."
                    />
                    <small class="text-muted">Mínimo 10 caracteres</small>
                </b-form-group>
            </div>
            <div slot="buttons">
                <b-button variant="secondary" @click="closeRejectModal">Cancelar</b-button>
                <b-button variant="danger" :disabled="!rejectForm.rejection_reason || rejectForm.rejection_reason.length < 10 || rejectSending" @click="submitReject">
                    <span v-if="rejectSending"><i class="fa fa-spinner fa-spin"></i> Enviando...</span>
                    <span v-else><i class="fa fa-times"></i> Rechazar y notificar</span>
                </b-button>
            </div>
        </app-pop-up>
    </div>
</template>
<script>
import SubTitle from '../../UI/Layout/SubTitle.vue';
import contractorManagementRepository from '../../../repositories/contractor-management.repository';

export default {
    name: 'ContractorManagementPage',
    components: {
        appSubTitle: SubTitle
    },
    data() {
        return {
            statusFilter: '',
            documentFilter: '',
            statusOptions: [
                { value: '', text: 'Todos' },
                { value: 'pending', text: 'Pendientes' },
                { value: 'approved', text: 'Aprobados' },
                { value: 'rejected', text: 'Rechazados' }
            ],
            fields: [
                { key: 'nombre', label: 'Nombre', sortable: false },
                { key: 'documento', label: 'Documento', sortable: false },
                { key: 'compania', label: 'Compañía', sortable: false },
                { key: 'fecha_inicio', label: 'Fecha de inicio de autorización', sortable: false },
                { key: 'fecha_fin', label: 'Fecha fin de autorización', sortable: false },
                { key: 'status', label: 'Estado', sortable: false },
                { key: 'reviewed_by', label: 'Revisado por', sortable: false },
                { key: 'actions', label: 'Acciones', sortable: false, thStyle: { width: '240px' } }
            ],
            items: [],
            loading: false,
            actionLoading: null,
            viewLoading: null,
            pagination: {
                current_page: 1,
                last_page: 1,
                per_page: 15,
                total: 0
            },
            rejectDocument: null,
            rejectForm: { rejection_reason: '' },
            rejectErrors: {},
            rejectSending: false,
            documentsData: { contractor: null, items: [] },
            documentsLoading: false,
            rejectInlineDoc: null,
            rejectInlineReason: '',
            rejectInlineError: ''
        };
    },
    computed: {
        documentsModalTitle() {
            const name = this.documentsData.contractor && this.documentsData.contractor.user
                ? this.documentsData.contractor.user.name
                : 'Contratista';
            return `Documentos de ${name}`;
        }
    },
    created() {
        this.loadDocuments();
    },
    methods: {
        applyFilters() {
            this.pagination.current_page = 1;
            this.loadDocuments();
        },
        loadDocuments() {
            this.loading = true;
            const params = { page: this.pagination.current_page, per_page: this.pagination.per_page };
            if (this.statusFilter) params.status = this.statusFilter;
            const doc = (this.documentFilter || '').trim();
            if (doc) params.document = doc;
            contractorManagementRepository.getDocuments(params)
                .then(res => {
                    const data = res.data;
                    if (data && data.data) {
                        this.items = data.data;
                        this.pagination.current_page = data.current_page;
                        this.pagination.last_page = data.last_page;
                        this.pagination.per_page = data.per_page;
                        this.pagination.total = data.total;
                    } else {
                        this.items = Array.isArray(data) ? data : [];
                    }
                })
                .catch(() => {
                    toastr.error('Error al cargar los documentos');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        onPageChange(page) {
            this.pagination.current_page = page;
            this.loadDocuments();
        },
        formatDate(value) {
            if (!value) return '';
            const d = new Date(value);
            return d.toLocaleDateString('es-CO');
        },
        isContractorDocumentExpired(doc) {
            if (!doc || !doc.expiry_date) return false;
            const end = new Date(doc.expiry_date);
            const today = new Date();
            end.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);
            return end < today;
        },
        statusLabel(status) {
            const map = {
                pending: 'Pendiente',
                pending_approval: 'Pendiente de aprobación',
                approved: 'Aprobado',
                rejected: 'Rechazado',
            };
            return map[status] || status;
        },
        statusVariant(status) {
            const map = {
                pending: 'secondary',
                pending_approval: 'warning',
                approved: 'success',
                rejected: 'danger',
            };
            return map[status] || 'secondary';
        },
        openDocumentsModal(rowItem) {
            this.documentsData = { contractor: null, items: [] };
            this.rejectInlineDoc = null;
            this.rejectInlineReason = '';
            this.rejectInlineError = '';
            this.$bus.$emit('contractor-documents-modal.open');
            const contractorId = rowItem.contractor_id || (rowItem.contractor && rowItem.contractor.id);
            if (!contractorId) {
                toastr.error('No se pudo identificar al contratista');
                return;
            }
            this.documentsLoading = true;
            contractorManagementRepository.getContractorDocuments(contractorId)
                .then(res => {
                    this.documentsData = res.data || { contractor: null, items: [] };
                })
                .catch(() => {
                    toastr.error('Error al cargar los documentos del contratista');
                    this.documentsData = { contractor: null, items: [] };
                })
                .finally(() => {
                    this.documentsLoading = false;
                });
        },
        closeDocumentsModal() {
            this.documentsData = { contractor: null, items: [] };
            this.rejectInlineDoc = null;
            this.rejectInlineReason = '';
            this.rejectInlineError = '';
        },
        docStatus(entry) {
            return entry.document ? entry.document.status : 'pending';
        },
        approveDocInModal(doc) {
            this.actionLoading = doc.id;
            contractorManagementRepository.approveDocument(doc.id)
                .then(res => {
                    if (res.data && res.data.status) {
                        toastr.success(res.data.msg || 'Documento aprobado');
                        const updated = res.data.document;
                        const entry = (this.documentsData.items || []).find(e => e.document && e.document.id === doc.id);
                        if (entry && updated) Object.assign(entry.document, updated);
                        // If backend marked the contractor_client pivot as approved, update the badge in the main table.
                        if (res.data.contractor_client_status) {
                            const contractorId = doc.contractor_id || (this.documentsData.contractor && this.documentsData.contractor.id);
                            if (contractorId) {
                                const mainRow = (this.items || []).find(e =>
                                    e.contractor_id === contractorId || (e.contractor && e.contractor.id === contractorId)
                                );
                                if (mainRow) mainRow.status = res.data.contractor_client_status;
                            }
                        }
                    } else {
                        toastr.error(res.data && res.data.msg ? res.data.msg : 'Error al aprobar');
                    }
                })
                .catch(err => {
                    const msg = (err.response && err.response.data && err.response.data.msg) || 'Error al aprobar';
                    toastr.error(msg);
                })
                .finally(() => {
                    this.actionLoading = null;
                });
        },
        toggleRejectInline(entry) {
            if (this.rejectInlineDoc && this.rejectInlineDoc.document && this.rejectInlineDoc.document.id === entry.document.id) {
                this.rejectInlineDoc = null;
                this.rejectInlineReason = '';
                this.rejectInlineError = '';
            } else {
                this.rejectInlineDoc = entry;
                this.rejectInlineReason = '';
                this.rejectInlineError = '';
            }
        },
        cancelRejectInline() {
            this.rejectInlineDoc = null;
            this.rejectInlineReason = '';
            this.rejectInlineError = '';
        },
        submitRejectInline() {
            const reason = (this.rejectInlineReason || '').trim();
            if (reason.length < 10) {
                this.rejectInlineError = 'Mínimo 10 caracteres';
                return;
            }
            if (!this.rejectInlineDoc || !this.rejectInlineDoc.document) return;
            this.rejectInlineError = '';
            this.rejectSending = true;
            contractorManagementRepository.rejectDocument(this.rejectInlineDoc.document.id, { rejection_reason: reason })
                .then(res => {
                    if (res.data && res.data.status) {
                        toastr.success(res.data.msg || 'Documento rechazado. Se ha notificado al contratista.');
                        const updated = res.data.document;
                        if (updated) Object.assign(this.rejectInlineDoc.document, updated);
                        if (res.data.contractor_client_status) {
                            const contractorId = this.rejectInlineDoc.document.contractor_id
                                || (this.documentsData.contractor && this.documentsData.contractor.id);
                            if (contractorId) {
                                const mainRow = (this.items || []).find(e =>
                                    e.contractor_id === contractorId || (e.contractor && e.contractor.id === contractorId)
                                );
                                if (mainRow) mainRow.status = res.data.contractor_client_status;
                            }
                        }
                        this.cancelRejectInline();
                    } else {
                        if (res.data && res.data.errors && res.data.errors.rejection_reason) {
                            this.rejectInlineError = res.data.errors.rejection_reason[0] || 'Error';
                        }
                        toastr.error(res.data && res.data.msg ? res.data.msg : 'Error al rechazar');
                    }
                })
                .catch(err => {
                    const data = err.response && err.response.data;
                    if (data && data.errors && data.errors.rejection_reason) {
                        this.rejectInlineError = Array.isArray(data.errors.rejection_reason) ? data.errors.rejection_reason[0] : data.errors.rejection_reason;
                    } else {
                        this.rejectInlineError = (data && data.msg) || 'Error al rechazar';
                    }
                    toastr.error(this.rejectInlineError);
                })
                .finally(() => {
                    this.rejectSending = false;
                });
        },
        viewDocument(doc) {
            this.viewLoading = doc.id;
            contractorManagementRepository.downloadDocument(doc.id)
                .then(res => {
                    const blob = res.data;
                    const url = window.URL.createObjectURL(blob);
                    const typeName = (doc.contractor_document_type && doc.contractor_document_type.name) || 'documento';
                    const ext = (doc.url_file && doc.url_file.split('.').pop()) || 'pdf';
                    const isViewable = /^(pdf|jpg|jpeg|png|gif|webp)$/i.test(ext);
                    if (isViewable) {
                        window.open(url, '_blank');
                        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                    } else {
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${typeName}_${doc.id}.${ext}`;
                        a.click();
                        window.URL.revokeObjectURL(url);
                    }
                })
                .catch(() => {
                    toastr.error('Error al cargar el archivo');
                })
                .finally(() => {
                    this.viewLoading = null;
                });
        },
        approve(doc) {
            this.actionLoading = doc.id;
            contractorManagementRepository.approveDocument(doc.id)
                .then(res => {
                    if (res.data && res.data.status) {
                        toastr.success(res.data.msg || 'Documento aprobado');
                        Object.assign(doc, res.data.document);
                    } else {
                        toastr.error(res.data && res.data.msg ? res.data.msg : 'Error al aprobar');
                    }
                })
                .catch(err => {
                    const msg = (err.response && err.response.data && err.response.data.msg) || 'Error al aprobar';
                    toastr.error(msg);
                })
                .finally(() => {
                    this.actionLoading = null;
                });
        },
        openRejectModal(doc) {
            this.rejectDocument = doc;
            this.rejectForm.rejection_reason = '';
            this.rejectErrors = {};
            this.$bus.$emit('contractor-management-reject-modal.open');
        },
        closeRejectModal() {
            this.rejectDocument = null;
            this.rejectForm.rejection_reason = '';
            this.rejectErrors = {};
            this.$bus.$emit('contractor-management-reject-modal.close');
        },
        submitReject() {
            const reason = (this.rejectForm.rejection_reason || '').trim();
            if (reason.length < 10) {
                this.rejectErrors = { rejection_reason: 'Mínimo 10 caracteres' };
                return;
            }
            if (!this.rejectDocument) return;
            this.rejectSending = true;
            this.rejectErrors = {};
            contractorManagementRepository.rejectDocument(this.rejectDocument.id, { rejection_reason: reason })
                .then(res => {
                    if (res.data && res.data.status) {
                        toastr.success(res.data.msg || 'Documento rechazado. Se ha notificado al contratista.');
                        Object.assign(this.rejectDocument, res.data.document);
                        this.closeRejectModal();
                    } else {
                        if (res.data && res.data.errors) this.rejectErrors = res.data.errors;
                        toastr.error(res.data && res.data.msg ? res.data.msg : 'Error al rechazar');
                    }
                })
                .catch(err => {
                    const data = err.response && err.response.data;
                    if (data && data.errors) this.rejectErrors = data.errors;
                    const msg = (data && data.msg) || 'Error al rechazar';
                    toastr.error(msg);
                })
                .finally(() => {
                    this.rejectSending = false;
                });
        }
    }
};
</script>
<style scoped>
/* Misma estructura label + control en ambas columnas; sin align-items-end que desplaza una columna */
.contractor-mgmt-filters .contractor-mgmt-filter-label {
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.5;
    color: #212529;
    display: block;
}
.contractor-mgmt-select-estado {
    width: 100%;
    min-width: 200px;
}
@media (min-width: 576px) {
    .contractor-mgmt-select-estado {
        width: 220px;
    }
}
/* Misma altura que .form-control del input-group (Bootstrap 4) */
.contractor-mgmt-filters >>> .custom-select {
    height: calc(1.5em + 0.75rem + 2px);
}
</style>
